/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * @deprecated This module is a bridge shim. Use logic hooks or `useGroupByStore(selector)` instead.
 * Removal deadline: end of Phase 6 cleanup PR.
 */
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { IS_FORECAST_TRUMPING_DEFAULT_CHART } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import {
  FORECAST_TRUMPING_FOLDER_ID,
  FORECAST_TRUMPING_FOLDER_NAME,
  MCDANIEL_SEARCH_FOLDER_NAME
} from "constants/settings.constants";

import { IGroupByListColumn, IGroupByListItem } from "models";
import { EntityKind } from "models/entityKind";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import type { GroupByT } from "./GroupBy";
import { GroupByStoreProvider, useGroupByStore } from "./GroupByStoreProvider";
import { selectActions } from "./groupBySelectors";

export type PropsT = Omit<GroupByT, "children" | "placement">;

export type MetadataFilterT = { key: string; value: string };
export type MetadataFiltersT = { [key: string]: string };

export const IGNORE_METADATA_FILTERS_FOR_GROUPS = ["Performance", "Forecast"];
export const MAX_SEARCH_RESULTS = 50;

type Action =
  | { type: "props"; payload: PropsT }
  | { type: "store_list_updated"; payload: IGroupByListItem[] }
  | { type: "selected_category"; payload: IGroupByListItem }
  | { type: "selected_field"; payload: IGroupByListColumn }
  | { type: "selected_product"; payload: string }
  | { type: "filter"; payload: string }
  | { type: "selected_forecast_folder"; payload: string }
  | { type: "selected_forecast_folder_name"; payload: string }
  | { type: "selected_rescat"; payload: string }
  | { type: "forecast_toggle"; payload: boolean }
  | { type: "selected_pden_source"; payload: PdenSourceEnum }
  | { type: "checked_fields"; payload: string[] }
  | { type: "custom_fields_edit_toggle"; payload: boolean }
  | { type: "custom_fields_edit_toggle_on" }
  | { type: "custom_fields_edit_toggle_off" }
  | { type: "selected_tab"; payload: string }
  | { type: "selected_metadata_filter"; payload: MetadataFilterT }
  | { type: "hovered_field"; payload: IGroupByListColumn };

export type Dispatch = (action: Action) => void;

export type State = {
  props: PropsT;
  allColumns: IGroupByListColumn[];
  categoryList: IGroupByListItem[];
  categoryColors: { [key: string]: string };
  selectedCategory: IGroupByListItem;
  selectedField: IGroupByListColumn;
  selectedProduct: string;
  filterText: string;
  isForecastToggleOn: boolean;
  selectedForecastFolder: string;
  selectedForecastFolderName: string;
  selectedRescat: string;
  selectedPdenSource: number;
  selectedStartDate: Maybe<Date>;
  selectedEndDate: Maybe<Date>;
  selectedMetadataFilters: MetadataFiltersT;
  checkedFields: string[];
  customFieldsEditToggle: boolean;
  selectedTab: string;
  isFiltering?: boolean;
  entityKind: EntityKind;
  hoveredField?: IGroupByListColumn;
};

type GroupByProviderProps = {
  children: React.ReactNode;
  entityKind?: EntityKind;
  chartType?: string;
};

/**
 * @deprecated Use `<GroupByStoreProvider>` instead.
 */
function GroupByProvider({
  children,
  entityKind = EntityKind.Well,
  chartType
}: GroupByProviderProps) {
  const isTrumpingDefault = IS_FORECAST_TRUMPING_DEFAULT_CHART(chartType as EvaChart);
  return (
    <GroupByStoreProvider
      overrides={{
        entityKind,
        isForecastToggleOn: entityKind === EntityKind.Well,
        selectedForecastFolder: isTrumpingDefault
          ? FORECAST_TRUMPING_FOLDER_ID
          : MCDANIEL_SEARCH_FOLDER_NAME,
        selectedForecastFolderName: isTrumpingDefault
          ? FORECAST_TRUMPING_FOLDER_NAME
          : MCDANIEL_SEARCH_FOLDER_NAME
      }}>
      {children}
    </GroupByStoreProvider>
  );
}

/**
 * @deprecated Use a logic hook or `useGroupByStore(selector)` instead.
 */
function useGroupByState(): State {
  return useGroupByStore(
    (s): State => ({
      props: s.props as PropsT,
      allColumns: s.allColumns ?? [],
      categoryList: s.categoryList ?? [],
      categoryColors: s.categoryColors ?? {},
      selectedCategory: s.selectedCategory as State["selectedCategory"],
      selectedField: s.selectedField as State["selectedField"],
      selectedProduct: s.selectedProduct as string,
      filterText: s.filterText,
      isForecastToggleOn: s.isForecastToggleOn,
      selectedForecastFolder: s.selectedForecastFolder,
      selectedForecastFolderName: s.selectedForecastFolderName,
      selectedRescat: s.selectedRescat,
      selectedPdenSource: s.selectedPdenSource,
      selectedStartDate: s.selectedStartDate,
      selectedEndDate: s.selectedEndDate,
      selectedMetadataFilters: s.selectedMetadataFilters as MetadataFiltersT,
      checkedFields: [...s.checkedFields],
      customFieldsEditToggle: s.customFieldsEditToggle,
      selectedTab: s.selectedTab,
      isFiltering: s.isFiltering,
      entityKind: s.entityKind,
      hoveredField: s.hoveredField ?? undefined
    })
  );
}

/**
 * @deprecated Use a logic hook's returned callbacks or `useGroupByStore(selectActions)` instead.
 */
function useGroupByUpdater(): Dispatch {
  const actions = useGroupByStore(useShallow(selectActions));

  return useMemo(
    () =>
      ((action: Action) => {
        const p = <T extends Action["type"]>(
          a: Action
        ): Extract<Action, { type: T }> extends { payload: infer P } ? P : never =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (a as any).payload;

        const handlers: Record<string, () => void> = {
          props: () => actions.setProps(p(action)),
          store_list_updated: () => actions.updateCategoryList(p(action)),
          selected_category: () => actions.setSelectedCategory(p(action)),
          selected_field: () => actions.setSelectedField(p(action)),
          selected_product: () => actions.setSelectedProduct(p(action)),
          filter: () => actions.setFilterText(p(action)),
          selected_forecast_folder: () => actions.setSelectedForecastFolder(p(action)),
          selected_forecast_folder_name: () =>
            actions.setSelectedForecastFolderName(p(action)),
          selected_rescat: () => actions.setSelectedRescat(p(action)),
          forecast_toggle: () => actions.setForecastToggle(p(action)),
          selected_pden_source: () => actions.setSelectedPdenSource(p(action)),
          checked_fields: () => actions.setCheckedFields(p(action)),
          custom_fields_edit_toggle: () => actions.setCustomFieldsEditToggle(p(action)),
          custom_fields_edit_toggle_on: () => actions.enableCustomFieldsEdit(),
          custom_fields_edit_toggle_off: () => actions.disableCustomFieldsEdit(),
          selected_tab: () => actions.setSelectedTab(p(action)),
          selected_metadata_filter: () => {
            const mf = p(action) as MetadataFilterT;
            actions.setMetadataFilter(mf.key, mf.value);
          },
          hovered_field: () => actions.setHoveredField(p(action))
        };
        const handler = handlers[action.type];
        if (handler) {
          handler();
        } else {
          throw new Error(`Invalid action type: ${action.type}`);
        }
      }) as Dispatch,
    [actions]
  );
}

export { GroupByProvider, useGroupByState, useGroupByUpdater };
